import { extent } from 'd3-array';

import { ValueGetter } from './DataProps';
import { isNumberCoercible } from './isNumberCoercible';
import { NumberCoercible } from './NumberCoercible';

interface GetExtentParameters<TValueType extends NumberCoercible, TDatum> {
  /**
   * The data set.
   */
  data: readonly Readonly<TDatum>[];
  /**
   * The value to return as the maximum, regardless of the data. If specified, the returned extent
   * will use this as the maximum value regardless of the values in the data set.
   */
  max?: TValueType;
  /**
   * The value to return as the minimum, regardless of the data. If specified, the returned extent
   * will use this as the minimum value regardless of the values in the data set.
   */
  min?: TValueType;
  /**
   * A function to retrieve an actual value from a datum in the set.
   */
  valueGetter: ValueGetter<TDatum, TValueType | undefined | null>;
}

/**
 * Returns the extent of a data set, deferring to a manually specified minimum and/or maximum, if
 * any.
 */
export const getExtent = <TValueType extends NumberCoercible, TDatum>({
  data,
  max,
  min,
  valueGetter,
}: GetExtentParameters<TValueType, TDatum>): [TValueType | number | undefined, TValueType | number | undefined] => {
  // Don't bother actually calculating the extent if both a min and max are specified already
  if (isNumberCoercible(min) && isNumberCoercible(max)) {
    return [min, max];
  }

  const [extentMin, extentMax] = extent(data, valueGetter);

  return [isNumberCoercible(min) ? min : extentMin, isNumberCoercible(max) ? max : extentMax];
};
